import React, { useEffect } from 'react';
import { t } from '@lingui/core/macro';
import styled from 'styled-components';

import Textarea from '@rover/components/src/formFields/Textarea';
import { Box, Button, Flex, Heading, Paragraph } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import Form from '@rover/react-lib/src/components/utils/Form';
import { useApiFlagSubmitCreate } from '@rover/rsdk/src/apiClient/latest/api-flag-submit-create';
import getFullUrl from '@rover/shared/js/utils/getFullUrl';

type Props = {
  onClose: () => void;
  onSuccessReport: () => void;
};

const StyledTextarea = styled(Textarea)`
  textarea {
    height: 200px;
    resize: none;
  }
`;

const SITTER_PROFILE_FLAG_TYPE = 'sitter_profile';
const MIN_WORDS = 4;

const ReportProfileForm = ({ onClose, onSuccessReport }: Props): JSX.Element => {
  const theme = useTheme();
  const mutation = useApiFlagSubmitCreate();
  useEffect(() => {
    if (mutation.isSuccess) onSuccessReport();
  }, [mutation.isSuccess]);

  const headerText = t`Report This Profile`;

  const initialData = {
    reportContent: '',
  };

  const validators = {
    reportContent: [Form.validators.required(), Form.validators.minWordCount(MIN_WORDS)],
  };

  const sendReply = (formContent: typeof initialData): void => {
    mutation.mutate({
      data: {
        comment: formContent.reportContent,
        flagType: SITTER_PROFILE_FLAG_TYPE,
        url: getFullUrl(),
      },
    });
  };

  return (
    <Form
      initialData={initialData}
      validators={validators}
      onSubmit={(values): void => {
        sendReply(values);
      }}
    >
      {(form): JSX.Element => (
        <>
          <Heading size="300">{headerText}</Heading>
          <Paragraph size="200">{t`Reason for reporting (required):`}</Paragraph>
          <Box>
            <StyledTextarea
              data-testid="report-profile-textarea"
              value={form.data.reportContent}
              onChange={(reportContent): void => form.onChange({ reportContent })}
              validationError={form.validationErrors.reportContent as string}
              validationType="inline"
            />
          </Box>
          <Flex
            flexDirection={['column', 'row', 'row']}
            pt="6x"
            width="100%"
            alignItems="center"
            justifyContent="center"
          >
            <Button
              fullWidth
              onClick={onClose}
              order={[2, 1, 1]}
              mt={['3x', '0x', '0x']}
              mr={['0x', '3x', '3x']}
              // The original kibble button has a shadow that crops the flex border
              // The design has a border instead, as implemented below
              sx={{
                border: `2px solid ${theme.colors.interactive_border.primary_base}`,
              }}
            >
              {t`Close`}
            </Button>
            <Button
              fullWidth
              onClick={(): void => form.onSubmit()}
              order={[1, 2, 2]}
              variant="primary"
            >
              {t`Submit`}
            </Button>
          </Flex>
        </>
      )}
    </Form>
  );
};

export default ReportProfileForm;
